/**
 * Grid Cell Component
 * Displays a single scale position with weight and group color
 */

import { useMScaleStore } from '../../store'
import { formatWeight } from '../../utils/format'

interface GridCellProps {
  position: number
  weight: number
  groupId: number | null
  isActive: boolean
  onClick?: () => void
  onHover?: () => void
}

const GROUP_COLORS = {
  0: '#00FF00', // Green
  1: '#0000FF', // Blue
  2: '#FFFF00', // Yellow
  3: '#FF0000', // Red
}

export function GridCell({ position, weight, groupId, isActive, onClick, onHover }: GridCellProps) {
  const { showHundredthsUnder10g } = useMScaleStore()

  // Determine border color based on group
  const borderColor = groupId !== null && groupId >= 0 && groupId <= 3
    ? GROUP_COLORS[groupId as keyof typeof GROUP_COLORS]
    : 'transparent'

  const borderWidth = groupId !== null ? '4px' : '2px'

  return (
    <button
      onClick={onClick}
      onMouseEnter={isActive && onHover ? onHover : undefined}
      disabled={!isActive}
      className={`
        aspect-square
        rounded-lg
        flex flex-col items-center justify-center
        transition-all
        ${isActive
          ? 'bg-white dark:bg-industrial-surface hover:shadow-lg cursor-pointer'
          : 'bg-gray-100 dark:bg-industrial-bg cursor-not-allowed opacity-50'
        }
        ${groupId !== null ? 'shadow-xl scale-105' : 'shadow-md'}
      `}
      style={{
        borderStyle: 'solid',
        borderWidth,
        borderColor,
      }}
    >
      {isActive ? (
        <>
          <div className="text-3xl md:text-4xl font-mono font-bold text-gray-900 dark:text-white weight-display">
            {formatWeight(weight, showHundredthsUnder10g)}
          </div>
          <div className="text-sm text-gray-500 dark:text-industrial-text-dim mt-1">
            g
          </div>
          <div className="text-xs text-gray-400 dark:text-industrial-text-dim mt-2">
            Pos {position}
          </div>
        </>
      ) : (
        <div className="text-center">
          <div className="text-2xl font-mono font-bold text-gray-400 dark:text-industrial-text-dim">
            --
          </div>
          <div className="text-xs text-gray-400 dark:text-industrial-text-dim mt-1">
            Inactive
          </div>
        </div>
      )}
    </button>
  )
}
